import React, { useEffect, useRef, useCallback, useState } from 'react';

declare global {
  interface Window {
    naver: typeof naver;
    // ISS-063 — Naver Maps 인증 실패 시 SDK 가 호출하는 글로벌 콜백.
    navermap_authFailure?: () => void;
  }
}

declare namespace naver {
  namespace maps {
    class Map {
      constructor(el: HTMLElement, opts: MapOptions);
      setCenter(latlng: LatLng): void;
      setZoom(zoom: number): void;
      panTo(latlng: LatLng): void;
    }
    class LatLng {
      constructor(lat: number, lng: number);
      lat(): number;
      lng(): number;
    }
    class Marker {
      constructor(opts: MarkerOptions);
      setMap(map: Map | null): void;
      getPosition(): LatLng;
      setPosition(pos: LatLng): void;
    }
    class Circle {
      constructor(opts: CircleOptions);
      setMap(map: Map | null): void;
      setCenter(pos: LatLng): void;
      setRadius(r: number): void;
    }
    class InfoWindow {
      constructor(opts: InfoWindowOptions);
      open(map: Map, marker: Marker): void;
      close(): void;
    }
    namespace Event {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      function addListener(target: unknown, type: string, handler: (...args: any[]) => void): unknown;
      function removeListener(handle: unknown): void;
    }
    interface MapOptions {
      center: LatLng;
      zoom: number;
      minZoom?: number;
      maxZoom?: number;
    }
    interface MarkerOptions {
      position: LatLng;
      map: Map;
      title?: string;
      draggable?: boolean;
      icon?: {
        content: string;
        anchor: Point;
      };
    }
    interface CircleOptions {
      map: Map;
      center: LatLng;
      radius: number;
      fillColor?: string;
      fillOpacity?: number;
      strokeColor?: string;
      strokeWeight?: number;
      strokeOpacity?: number;
    }
    interface InfoWindowOptions {
      content: string;
      borderWidth?: number;
    }
    class Point {
      constructor(x: number, y: number);
    }
  }
}

export interface MapMarker {
  id: string;
  lat: number;
  lng: number;
  radius?: number;
  label?: string;
}

interface MapViewProps {
  markers: MapMarker[];
  center?: { lat: number; lng: number };
  zoom?: number;
  onPinDrag?: (coord: { lat: number; lng: number }) => void;
  editablePin?: { lat: number; lng: number; radius: number };
  height?: number;
  onClick?: (coord: { lat: number; lng: number }) => void;
  onMarkerClick?: (markerId: string) => void;
}

const SDK_SCRIPT_ID = 'naver-maps-sdk';

// ISS-063 — 인증 실패 글로벌 상태. 같은 Client ID 로 여러 MapView 인스턴스가
// 마운트될 수 있으므로 모듈 스코프에서 관리한다.
// SDK 가 한 번 인증 실패하면 같은 페이지 라이프타임 동안 재시도해도 무의미.
let sdkAuthFailed = false;
const authFailureSubscribers = new Set<() => void>();

function notifyAuthFailure(): void {
  sdkAuthFailed = true;
  authFailureSubscribers.forEach((fn) => {
    try { fn(); } catch { /* 구독자 측 throw 격리 */ }
  });
}

// 글로벌 콜백 1회 등록 (HMR 안전).
if (typeof window !== 'undefined' && !window.navermap_authFailure) {
  window.navermap_authFailure = notifyAuthFailure;
}

function getClientId(): string {
  return window.__ENV__?.REACT_APP_NAVER_MAPS_CLIENT_ID
    || process.env.REACT_APP_NAVER_MAPS_CLIENT_ID
    || '';
}

function loadNaverMapsSDK(): Promise<boolean> {
  if (sdkAuthFailed) return Promise.resolve(false);
  if (window.naver?.maps) return Promise.resolve(true);

  const clientId = getClientId();
  if (!clientId) return Promise.resolve(false);

  if (document.getElementById(SDK_SCRIPT_ID)) {
    return new Promise((resolve) => {
      const check = setInterval(() => {
        if (sdkAuthFailed) { clearInterval(check); resolve(false); return; }
        if (window.naver?.maps) { clearInterval(check); resolve(true); }
      }, 100);
      setTimeout(() => { clearInterval(check); resolve(false); }, 5000);
    });
  }

  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.id = SDK_SCRIPT_ID;
    script.type = 'text/javascript';
    // ISS-064 — NCP Maps API v3 표준 SDK URL.
    // 구버전 (openapi.map.naver.com + ncpClientId) 은 클래식 콘솔 발급분 전용이며,
    // 신규 NCP 콘솔 Application 의 Client ID 는 호스트 oapi.map.naver.com + 파라미터 ncpKeyId 로만 인증된다.
    // (공식: https://navermaps.github.io/maps.js.ncp/docs/tutorial-2-Getting-Started.html)
    script.src = `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${clientId}`;
    script.onload = () => {
      const check = setInterval(() => {
        if (sdkAuthFailed) { clearInterval(check); resolve(false); return; }
        if (window.naver?.maps) { clearInterval(check); resolve(true); }
      }, 50);
      setTimeout(() => { clearInterval(check); resolve(false); }, 5000);
    };
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });
}

const MapView: React.FC<MapViewProps> = ({
  markers,
  center,
  zoom = 14,
  onPinDrag,
  editablePin,
  height = 500,
  onClick,
  onMarkerClick,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<naver.maps.Map | null>(null);
  const overlaysRef = useRef<Array<naver.maps.Marker | naver.maps.Circle>>([]);
  const editOverlaysRef = useRef<Array<naver.maps.Marker | naver.maps.Circle>>([]);
  // ISS-063 — Event.addListener 가 반환하는 핸들 추적. cleanup 시 removeListener 로 회수.
  const listenersRef = useRef<unknown[]>([]);
  const [sdkReady, setSdkReady] = useState(!sdkAuthFailed && !!window.naver?.maps);
  const [sdkFailed, setSdkFailed] = useState(false);
  // ISS-063 — sdkFailed 가 true 일 때 인증 실패인지 (메시지 분리용)
  const [authFailed, setAuthFailed] = useState(sdkAuthFailed);

  const clearOverlays = useCallback((arr: Array<naver.maps.Marker | naver.maps.Circle>) => {
    // ISS-060 — Naver Maps SDK cleanup race condition 보호.
    // unmount 시점에 SDK 내부 이벤트 시스템이 부분 해제되어 setMap(null) 이 TypeError 를
    // throw 하는 케이스 (Console: "Cannot read properties of null (reading 'capitalize')").
    // React commit 단계에서 이 throw 가 propagate 되면 후속 렌더링 중단 → 흰 화면.
    arr.forEach((o) => {
      try {
        o.setMap(null);
      } catch {
        /* SDK 내부 정리 race — 이미 detach 된 상태이므로 무시 */
      }
    });
    arr.length = 0;
  }, []);

  const removeAllListeners = useCallback(() => {
    // ISS-063 — Event.addListener 핸들 회수. SDK 내부 dispatch 큐에 detach 된 marker 참조가
    // 남아 있으면 이후 dispatch 시 'capitalize' TypeError 가능 → cleanup 시 명시 회수.
    listenersRef.current.forEach((h) => {
      try {
        window.naver?.maps?.Event?.removeListener?.(h);
      } catch {
        /* SDK 부분 해제 — 무시 */
      }
    });
    listenersRef.current = [];
  }, []);

  // ISS-063 — 글로벌 인증 실패 발생 시 본 인스턴스에도 즉시 반영.
  useEffect(() => {
    if (sdkAuthFailed) {
      setAuthFailed(true);
      setSdkFailed(true);
      return;
    }
    const onAuthFail = () => {
      setAuthFailed(true);
      setSdkFailed(true);
    };
    authFailureSubscribers.add(onAuthFail);
    return () => { authFailureSubscribers.delete(onAuthFail); };
  }, []);

  useEffect(() => {
    if (sdkReady || sdkAuthFailed) return;
    let cancelled = false;
    loadNaverMapsSDK().then((ok) => {
      if (cancelled) return;
      if (ok) setSdkReady(true);
      else setSdkFailed(true);
    });
    return () => { cancelled = true; };
  }, [sdkReady]);

  useEffect(() => {
    if (!sdkReady || sdkAuthFailed || !containerRef.current || !window.naver?.maps) return;

    let mapInstance: naver.maps.Map | null = null;
    try {
      const defaultCenter = center ?? { lat: 37.5665, lng: 126.978 };
      mapInstance = new window.naver.maps.Map(containerRef.current, {
        center: new window.naver.maps.LatLng(defaultCenter.lat, defaultCenter.lng),
        zoom,
      });
      mapRef.current = mapInstance;
    } catch {
      // ISS-063 — Map 생성자 자체가 SDK 부분 초기화 상태에서 throw 가능.
      setSdkFailed(true);
      return;
    }

    // ISS-028 — cleanup 시점의 ref 값이 effect 실행 시점과 다를 수 있어 effect 진입 시 스냅샷 저장.
    const overlaysSnapshot = overlaysRef.current;
    const editOverlaysSnapshot = editOverlaysRef.current;

    return () => {
      // ISS-063 — cleanup 전체 try/catch. clearOverlays 외 어떤 SDK 메서드 호출도 throw 가능.
      try {
        removeAllListeners();
        clearOverlays(overlaysSnapshot);
        clearOverlays(editOverlaysSnapshot);
        mapRef.current = null;
      } catch {
        /* SDK race — 이미 detach */
      }
    };
  }, [sdkReady]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const map = mapRef.current;
    if (!map || sdkAuthFailed || !window.naver?.maps) return;

    try {
      clearOverlays(overlaysRef.current);

      markers.forEach((m) => {
        const pos = new window.naver.maps.LatLng(m.lat, m.lng);
        const marker = new window.naver.maps.Marker({
          position: pos,
          map,
          title: m.label,
        });
        overlaysRef.current.push(marker);

        if (onMarkerClick) {
          const handle = window.naver.maps.Event.addListener(marker, 'click', () => {
            onMarkerClick(m.id);
          });
          listenersRef.current.push(handle);
        } else if (m.label) {
          const infoWindow = new window.naver.maps.InfoWindow({
            content: `<div style="padding:4px 8px;font-size:12px;white-space:nowrap">${(m.label || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;')}</div>`,
            borderWidth: 1,
          });
          const handle = window.naver.maps.Event.addListener(marker, 'click', () => {
            try { infoWindow.open(map, marker); } catch { /* SDK race */ }
          });
          listenersRef.current.push(handle);
        }

        if (m.radius) {
          const circle = new window.naver.maps.Circle({
            map,
            center: pos,
            radius: m.radius,
            fillColor: '#F44336',
            fillOpacity: 0.12,
            strokeColor: '#F44336',
            strokeWeight: 1,
            strokeOpacity: 0.5,
          });
          overlaysRef.current.push(circle);
        }
      });
    } catch {
      // ISS-063 — 마커/서클 생성 중 SDK throw → fallback 으로 전환.
      setSdkFailed(true);
    }
  }, [markers, clearOverlays, sdkReady, onMarkerClick]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || sdkAuthFailed || !window.naver?.maps) return;
    if (center) {
      try {
        map.panTo(new window.naver.maps.LatLng(center.lat, center.lng));
      } catch {
        /* SDK race */
      }
    }
  }, [center]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || sdkAuthFailed || !window.naver?.maps) return;

    try {
      clearOverlays(editOverlaysRef.current);

      if (!editablePin) return;

      const pos = new window.naver.maps.LatLng(editablePin.lat, editablePin.lng);
      const marker = new window.naver.maps.Marker({
        position: pos,
        map,
        draggable: true,
        icon: {
          content: '<div style="width:14px;height:14px;border-radius:50%;background:#4CAF50;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,.3)"></div>',
          anchor: new window.naver.maps.Point(7, 7),
        },
      });
      editOverlaysRef.current.push(marker);

      const circle = new window.naver.maps.Circle({
        map,
        center: pos,
        radius: editablePin.radius,
        fillColor: '#4CAF50',
        fillOpacity: 0.15,
        strokeColor: '#4CAF50',
        strokeWeight: 1,
        strokeOpacity: 0.5,
      });
      editOverlaysRef.current.push(circle);

      if (onPinDrag) {
        const handle = window.naver.maps.Event.addListener(marker, 'dragend', () => {
          try {
            const p = marker.getPosition();
            circle.setCenter(p);
            onPinDrag({ lat: p.lat(), lng: p.lng() });
          } catch { /* SDK race */ }
        });
        listenersRef.current.push(handle);
      }
    } catch {
      setSdkFailed(true);
    }
  }, [editablePin, onPinDrag, clearOverlays, sdkReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || sdkAuthFailed || !window.naver?.maps || !onClick) return;
    try {
      const handle = window.naver.maps.Event.addListener(map, 'click', (e: { coord: naver.maps.LatLng }) => {
        try { onClick({ lat: e.coord.lat(), lng: e.coord.lng() }); } catch { /* */ }
      });
      listenersRef.current.push(handle);
    } catch {
      /* SDK race */
    }
  }, [onClick, sdkReady]);

  // ISS-063 — fallback UI 분기:
  //   1. 인증 실패 (authFailed): "지도 인증에 실패했습니다 / 잠시 후 다시 시도해 주세요"
  //   2. ClientID 미설정: "REACT_APP_NAVER_MAPS_CLIENT_ID 설정이 필요합니다"
  //   3. 그 외 SDK 실패 (네트워크/스크립트 로드 실패): "지도를 표시할 수 없습니다"
  if (sdkFailed || (!sdkReady && !getClientId())) {
    let title = '지도를 표시할 수 없습니다';
    let subtitle = '';
    if (authFailed) {
      title = '지도 인증에 실패했습니다';
      subtitle = '잠시 후 다시 시도해 주세요';
    } else if (!getClientId()) {
      subtitle = 'REACT_APP_NAVER_MAPS_CLIENT_ID 설정이 필요합니다';
    } else {
      subtitle = '네트워크 또는 SDK 로드 오류';
    }
    return (
      <div style={{ width: '100%', height, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', borderRadius: 8, border: '1px dashed #ccc' }}>
        <div style={{ textAlign: 'center', color: '#999' }}>
          <p style={{ margin: 0, fontSize: 14 }}>{title}</p>
          {subtitle && <p style={{ margin: '4px 0 0', fontSize: 12 }}>{subtitle}</p>}
        </div>
      </div>
    );
  }

  if (!sdkReady) {
    return (
      <div style={{ width: '100%', height, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', borderRadius: 8 }}>
        <p style={{ color: '#999' }}>지도 로딩 중...</p>
      </div>
    );
  }

  return <div ref={containerRef} style={{ width: '100%', height, borderRadius: 8 }} />;
};

export default MapView;
